import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../AppContext";
import { isKnmtPractice, formatStandard } from "../onboarding/onboarding";
import { DEFAULT_RANGE_BOUNDS } from "../common/helpers";
import HowToVideo from "./HowToVideo";

const HelpPage = () => {
  const { t } = useTranslation();
  const { currentUser, currentOrg, setWelcomeOpen } = useAppContext();
  const [query, setQuery] = useState("");

  const isAdmin = currentUser?.role === "superadmin" || currentUser?.role === "admin";
  const [lowMax, mediumMax] = currentOrg?.rangeBounds?.length === 2 ? currentOrg.rangeBounds : DEFAULT_RANGE_BOUNDS;
  const standard = formatStandard(currentOrg?.securityStandard);

  const steps = [
    { icon: "icon-Risico", title: t("help.step_risks_title"), text: t("help.step_risks_text"), to: "/risks", cta: t("risks_plural") },
    { icon: "icon-Maatregelen", title: t("help.step_measures_title"), text: t("help.step_measures_text"), to: "/measures", cta: t("measures") },
    { icon: "icon-evaluation", title: t("help.step_evaluate_title"), text: t("help.step_evaluate_text"), to: isAdmin ? "/prepare-evaluation" : null, cta: t("rep_menu") },
  ];

  const terms = useMemo(() => [
    { term: t("help.term_risk"), text: t("help.term_risk_text"), note: t("help.term_risk_note") },
    {
      term: t("help.term_measure"),
      text: t("help.term_measure_text"),
      chips: ["todo", "in_progress", "on_hold", "implemented", "dismissed"].map((key) => t(key)),
      note: t("help.term_measure_note"),
    },
    { term: t("risk_owner"), text: t("help.term_risk_owner_text") },
    { term: t("measure_owner"), text: t("help.term_measure_owner_text") },
    { term: t("help.term_cia"), text: t("help.term_cia_text") },
    { term: t("help.term_likelihood_impact"), text: t("help.term_likelihood_impact_text") },
    {
      term: t("help.term_risk_level"),
      text: t("help.term_risk_level_text"),
      levels: [
        { className: "low", label: t("low"), range: `1 – ${lowMax}` },
        { className: "medium", label: t("medium"), range: `${lowMax + 1} – ${mediumMax}` },
        { className: "high", label: t("high"), range: `${mediumMax + 1} – 25` },
      ],
    },
    { term: t("help.term_scores"), text: t("help.term_scores_text") },
    { term: t("help.term_treatment"), text: t("help.term_treatment_text"), chips: ["reduce", "accept", "temporarily_accept", "avoid", "transfer"].map((key) => t(key)) },
    { term: t("domain"), text: t("help.term_domain_text") },
    { term: t("help.term_soa"), text: t("help.term_soa_text") },
    { term: t("rep_menu"), text: t("rep_subtitle") },
    { term: standard, text: t("help.term_standard_text", { standard }) },
    { term: t("help.term_copilot"), text: t("help.term_copilot_text") },
  ], [t, lowMax, mediumMax, standard]);

  const needle = query.trim().toLowerCase();
  const visibleTerms = needle
    ? terms.filter(({ term, text, chips, note }) => [term, text, note || "", ...(chips || [])].some((value) => value.toLowerCase().includes(needle)))
    : terms;

  return (
    <div id="content">
      <div className="help-page">
        <header className="help-header">
          <div>
            <h1><i className="icon-idea" aria-hidden="true" /> {t("help.title")}</h1>
            <p>{t("help.subtitle")}</p>
          </div>
          {isKnmtPractice(currentUser) && (
            <button type="button" className="help-tour-btn" onClick={() => setWelcomeOpen(true)}>
              <i className="icon-play" aria-hidden="true" /> {t("help.replay_tour")}
            </button>
          )}
        </header>

        <section aria-labelledby="help-how">
          <h2 id="help-how" className="help-section-title">{t("help.how_title")}</h2>
          <div className="help-video help-card">
            <HowToVideo />
            <p><i className="icon-play" aria-hidden="true" /> {t("help.video_caption")}</p>
          </div>
          <ol className="help-steps">
            {steps.map((step, i) => (
              <li key={step.to || i} className="help-card">
                <span className="help-step-nr">{i + 1}</span>
                <i className={`help-step-icon ${step.icon}`} aria-hidden="true" />
                <h3>{step.title}</h3>
                <p>{step.text}</p>
                {step.to && (
                  <Link to={step.to} className="help-step-link">
                    {step.cta} <i className="icon-arrow-right" aria-hidden="true" />
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="help-terms">
          <div className="help-terms-bar">
            <h2 id="help-terms" className="help-section-title">{t("help.terms_title")}</h2>
            <label className="help-search">
              <i className="icon-search" aria-hidden="true" />
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("help.search_placeholder")} aria-label={t("help.search_placeholder")} />
            </label>
          </div>
          <dl className="help-terms help-card">
            {visibleTerms.map(({ term, text, levels, chips, note }) => (
              <div key={term} className="help-term">
                <dt>{term}</dt>
                <dd>
                  {text}
                  {levels && (
                    <span className="help-levels">
                      {levels.map((level) => (
                        <span key={level.className} className={`help-level ${level.className}`}>{level.label} <small>{level.range}</small></span>
                      ))}
                    </span>
                  )}
                  {chips && (
                    <span className="help-chips">
                      {chips.map((chip) => <span key={chip}>{chip}</span>)}
                    </span>
                  )}
                  {note && <span className="help-term-note">{note}</span>}
                </dd>
              </div>
            ))}
            {visibleTerms.length === 0 && <p className="help-empty">{t("help.no_results")}</p>}
          </dl>
        </section>

        <section className="help-contact help-card">
          <i className="icon-mail" aria-hidden="true" />
          <div>
            <h2>{t("need_help")}</h2>
            <p>{t("help.contact_text")}</p>
          </div>
          <a href="mailto:info@risqui.nl" className="help-tour-btn">info@risqui.nl</a>
        </section>
      </div>
    </div>
  );
};

export default HelpPage;
